/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { AxiosError } from "axios"
import MedaicaApiService from "./"
import { PatientReportFacts, PatientReportSession, PatientReportVerifyResult } from "@medaica/common/types"

/**
 * The patient report download page's API (`app/patient_report/routes.py`).
 * The three gate calls (createSession / verify / resendCode) go over
 * `apiClient(false)`; the session calls need a MedaicaApiService whose
 * `accessTokenProvider` returns the download session token — a private,
 * token-closure instance like the booking wizard's, never the global
 * Auth0-wired service.
 *
 * The raw link token from the emailed URL is always POSTed in a body, never
 * sent as a query string to the API.
 */
class PatientReportApi {
  private readonly _apiService: MedaicaApiService

  constructor(apiService: MedaicaApiService) {
    this._apiService = apiService
  }

  // --- gate (unauthenticated) ---

  /** Resolve the emailed link and send a code to the address on file. Throws INVALID_REPORT_LINK for a dead link. */
  async createSession(token: string): Promise<PatientReportSession> {
    const apiClient = await this._apiService.apiClient(false)
    const response = await apiClient.post<PatientReportSession>("/patient-report/session", { token })
    return response.data
  }

  async verify(verificationToken: string, code: string): Promise<PatientReportVerifyResult> {
    const apiClient = await this._apiService.apiClient(false)
    const response = await apiClient.post<PatientReportVerifyResult>("/patient-report/verify", {
      verificationToken,
      code,
    })
    return response.data
  }

  async resendCode(verificationToken: string): Promise<{ codeExpiresAt: Date }> {
    const apiClient = await this._apiService.apiClient(false)
    const response = await apiClient.post<{ codeExpiresAt: Date }>("/patient-report/resend-code", {
      verificationToken,
    })
    return response.data
  }

  // --- session (Authorization: Bearer <download session token>) ---

  /** The download screen's header facts; 410 once the release is reverted. */
  async getFacts(): Promise<PatientReportFacts> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<PatientReportFacts>("/patient-report")
    return response.data
  }

  /** The stored report PDF as a Blob; 410 once the release is reverted. */
  async getPdf(): Promise<Blob> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<Blob>("/patient-report/pdf", { responseType: "blob" })
    return response.data
  }

  /**
   * The stored clinician-format PDF (the copy for the patient's own doctor) as a Blob; 410 once
   * the release is reverted, 404 when the release carries none (`providerReport` is null).
   */
  async getProviderPdf(): Promise<Blob> {
    const apiClient = await this._apiService.apiClient()
    const response = await apiClient.get<Blob>("/patient-report/provider-pdf", { responseType: "blob" })
    return response.data
  }
}

/** True for the 410 the session calls return once the release was reverted. */
const isReportGone = (err: unknown): boolean => (err as AxiosError).response?.status === 410

export default PatientReportApi
export { isReportGone }
